"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building2, ChevronsLeft, ChevronsRight, LogOut, Menu, MessagesSquare, User as UserIcon } from "lucide-react";
import { IconBook, IconChevronDown, IconDashboard, IconHome, IconSettings, IconUsers } from "@/components/ui/icons";
import { useAuth } from "@/components/auth-provider";
import { TopPanel } from "@/components/top-panel";
import { useIsDesktop } from "@/lib/use-is-desktop";
import { usePresence } from "@/lib/use-presence";
import { useTheme } from "@/components/theme-provider";
import { естьПраваВОтделе, hasPermission, имеетПравоЯвно, ПРАВО_ГЛАВНАЯ, ПРАВО_ИНСТРУКЦИИ, ПРАВО_КАБИНЕТ, ПРАВО_КОМАНДА } from "@/lib/access";
import { glassPillStyle } from "@/lib/theme";
import { deptColorOf } from "@/lib/dept-colors";
import { api, API_BASE } from "@/lib/api";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useDropDirection } from "@/lib/use-drop-direction";
import { DevRequestFab } from "@/components/dev-request-fab";
import { StickyHScrollbar } from "@/components/ui/sticky-hscrollbar";
import type { Department, PortalModule } from "@/lib/types";
import { DEPT_ICON, fallbackDeptIcon } from "@/components/home/home-content";
import { useOutsideClick } from "@/lib/use-outside-click";
import { Z } from "@/lib/z";
import { TopProgress } from "@/components/ui/top-progress";
import { MovingHighlight } from "@/components/ui/moving-highlight";
import { Collapse } from "@/components/ui/collapse";
import { pickDeptModules } from "@/components/departments/departments-workspace";
import { hasDeptDashboard } from "@/lib/dept-tabs";
import { visibleDeptTabs } from "@/lib/dept-sections";
import {
  markOpenDeptDashboard, markOpenDeptModule,
  subscribeActiveSection, type ActiveSection,
} from "@/lib/dept-nav";

const SIDEBAR_STORAGE_KEY = "company-portal:sidebar-collapsed";

const roleTitles: Record<string, string> = {
  employee: "Сотрудник",
  department_head: "Руководитель",
  platform_admin: "Администратор",
};

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { mode, theme } = useTheme();
  const { user, logout } = useAuth();
  // Отметка присутствия — только для вошедших: кто онлайн и сколько времени
  // провёл в портале (см. lib/use-presence).
  usePresence(Boolean(user));
  const [collapsed, setCollapsed] = useState(true);
  /** Переключение бара с ЗАПИСЬЮ в хранилище.
   *
   *  ⚠️ ПОЧЕМУ НЕ ЭФФЕКТОМ. Сохранение стояло в `useEffect([collapsed])`, и на
   *  монтировании он срабатывал с НАЧАЛЬНЫМ значением (свёрнут), затирая
   *  сохранённое «раскрыт» ещё до того, как эффект-восстановление успевал его
   *  прочитать. В dev-режиме React вызывает эффекты дважды, и второе чтение
   *  видело уже затёртую единицу — бар открывался свёрнутым при каждой
   *  перезагрузке (Максим, 27.08.2026). Пишем только осознанное переключение. */
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0"); } catch { /* приватный режим */ }
      return next;
    });
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [modules, setModules] = useState<PortalModule[]>([]);
  // ── Модули для сайдбара (структура референса PLUX) ─────────────────────────
  // Навигация переезжает влево: отделы списком, модули раскрытием под ними.
  // Отбор тот же, что на странице отдела (pickDeptModules) — списки обязаны
  // совпадать, иначе в меню появятся пункты, которых на странице нет.
  const [portalModules, setPortalModules] = useState<PortalModule[]>([]);
  const [openDept, setOpenDept] = useState<string | null>(null);
  // Список разделов сайдбар считает САМ для любого отдела — тем же расчётом,
  // что и страница отдела (lib/dept-sections). От страницы приходит только то,
  // что она одна и знает: какой раздел сейчас открыт.
  const [activeSection, setActiveSection] = useState<ActiveSection>({ deptKey: "", moduleKey: null });
  useEffect(() => subscribeActiveSection(setActiveSection), []);
  useEffect(() => { api.getModules().then(setPortalModules).catch(() => setPortalModules([])); }, []);
  // Высота строки профиля: на неё единая рамка вылезает вверх, накрывая кнопку.
  const [deptsFlyout, setDeptsFlyout] = useState(false);
  // Флайаут отделов у СВЁРНУТОГО рельса: список выезжает вправо, сам бар при этом
  // остаётся узким (правка Олега 21.07 — раньше наведение разъезжало весь бар).
  const [deptsFly, setDeptsFly] = useState(false);
  // РАЗДЕЛЬНЫЕ рефы для desktop/mobile: SidebarContent рендерится ДВАЖДЫ
  // (десктопный aside всегда в DOM через `hidden lg:flex`). Общий ref указывал бы
  // на скрытый экземпляр → useOutsideClick(mousedown) закрывал бы попап ДО клика по
  // ссылке (клик проваливался на оверлей, drawer закрывался, навигации нет).
  const deptsRefDesktop = useRef<HTMLDivElement>(null);
  const deptsRefMobile = useRef<HTMLDivElement>(null);
  // Панель в шапке — третий экземпляр рядом с рельсом и мобильным меню: свои
  // refs, иначе useOutsideClick чужого экземпляра закрывает попап до клика.
  useOutsideClick([deptsRefDesktop, deptsRefMobile], () => setDeptsFlyout(false), deptsFlyout);
  useOutsideClick([deptsRefDesktop, deptsRefMobile], () => setDeptsFly(false), deptsFly);
  /** Сколько места у флайаута отделов до низа экрана.
   *
   *  ⚠️ ПОЧЕМУ НЕ `max-h-[calc(100vh-24px)]`. Панель висит на кнопке «Отделы»,
   *  а та стоит не у верхнего края: высота в «весь экран минус отступ» уводила
   *  нижние разделы ЗА край, и добраться до них было нечем — прокрутка панели
   *  тоже оказывалась за экраном. С раскрытыми разделами список стал длинным,
   *  и это перестало быть теорией. Меряем от верха самой панели. */
  const [deptsFlyMaxH, setDeptsFlyMaxH] = useState(480);
  useEffect(() => {
    if (!deptsFly || typeof window === "undefined") return;
    const measure = () => {
      // Экземпляров рельса два (десктопный и мобильный), меряем ВИДИМЫЙ.
      const node = [deptsRefDesktop.current, deptsRefMobile.current]
        .find((n) => n && n.getClientRects().length > 0);
      if (!node) return;
      setDeptsFlyMaxH(Math.max(160, Math.round(window.innerHeight - node.getBoundingClientRect().top - 16)));
    };
    measure();
    window.addEventListener("resize", measure);
    // Прокрутка страницы двигает якорь — пересчитываем и на неё.
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [deptsFly]);
  const rawLogoUrl = mode === "dark" ? (theme.logoUrlDark ?? theme.logoUrlLight) : (theme.logoUrlLight ?? theme.logoUrlDark);
  const logoSrc = rawLogoUrl ? (rawLogoUrl.startsWith("/") ? `${API_BASE}${rawLogoUrl}` : rawLogoUrl) : null;
  // Десктоп-сайдбар — узкий рельс (кружки). НАВЕДЕНИЕ НА БАР ЕГО НЕ РАСКРЫВАЕТ
  // (правка Олега 21.07: полное разъезжание от случайного заезда мышью мешало) —
  // у свёрнутого рельса отделы/профиль/уведомления показываются ФЛАЙАУТАМИ вправо,
  // у остальных пунктов подписи в `title`. Бар раскрыт: если ЗАКРЕПЛЁН
  // (pinned = !collapsed) либо открыт аккордеон отделов развёрнутого вида.
  const deskExpanded = !collapsed || deptsFlyout;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    setCollapsed(stored == null ? true : stored === "1");
  }, []);

  useEffect(() => {
    Promise.all([api.getDepartments(), api.getModules()])
      .then(([depts, mods]) => {
        setDepartments(depts);
        setModules(mods);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "platform_admin") {
      document.body.classList.add("is-platform-admin");
    } else {
      document.body.classList.remove("is-platform-admin");
    }
  }, [user?.role]);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Departments visible in sidebar: own department only (admins see all)
  const accessibleDepts = useMemo(() => {
    if (!user) return [];
    // "system" — служебный отдел для platform_admin'ов, не показываем в sidebar.
    const HIDDEN_DEPTS = new Set(["system"]);
    // ⚠️ ПУСТОЙ ОТДЕЛ В МЕНЮ НЕ ПОКАЗЫВАЕМ (Максим, 04.09.2026). У IT после
    // переезда задач в кабинет не осталось ни одного раздела: пункт раскрывался
    // в пустоту. Правило общее, не про IT: отдел появляется в меню, только если
    // внутри есть хоть что-то — раздел или свой дашборд. Отдел с разделами, к
    // которым у человека нет прав, тоже пропадает — и правильно: показывать
    // дверь, за которой пусто, незачем.
    const непустой = (d: { key: string }) =>
      hasDeptDashboard(d.key) || естьПраваВОтделе(user, d.key) || pickDeptModules(portalModules, d.key, user).length > 0;
    const visible = departments.filter((d) => !HIDDEN_DEPTS.has(d.key) && непустой(d));
    // ⚠️ АДМИНИСТРАТОРСКОГО ОБХОДА ЗДЕСЬ НЕТ (Максим, 04.09.2026: «нужно, чтобы
    // видел только я»). Обычно роль platform_admin открывает всё, но
    // администраторов на портале четверо, а список отделов в меню должен быть
    // ровно тем, что выдано в матрице. Доступ к данным роль по-прежнему даёт —
    // это про НАВИГАЦИЮ, а не про права на данные.
    // ⚠️ МЕНЮ СЧИТАЕТСЯ ПО ПРАВАМ, А НЕ ПО ЗАПИСИ «ОТДЕЛ» В УЧЁТКЕ (Максим,
    // 04.09.2026: «почему Киямова не видит отдел продаж, если у неё есть доступ
    // в ролях и правах?»). Раньше показывался ровно один отдел — тот, что
    // записан в карточке. У Алины Киямовой это HR, а прав на «Продажи» у неё
    // тринадцать: раздел был открыт, но в меню его не было, и попасть туда
    // можно было только по прямой ссылке. Теперь показываем свой отдел И все,
    // где человеку что-то выдано.
    // ⚠️ РЕШАЕТ ТОЛЬКО МАТРИЦА (Максим, 04.09.2026: «включить это нужно через
    // стандартные методы в ролях и доступах»). Исключение «свой отдел виден
    // всегда» убрано: с ним человек видел раздел, к которому прав не выдавали,
    // просто потому, что так записано у него в карточке.
    return visible.filter((d) => естьПраваВОтделе(user, d.key));
  }, [departments, user, portalModules]);

  // Где сотрудник сейчас в портале — контекст для AI-ассистента (понимает «эта вкладка/что тут»).
  const aiLocation = useMemo(() => {
    if (pathname === "/home" || pathname === "/") return "Главная страница портала";
    if (pathname === "/cabinet") return "Кабинет — личные задачи, встречи и цели";
    if (pathname.startsWith("/help")) return "Раздел «Инструкции» (справка портала)";
    const dm = pathname.match(/^\/departments\/([^/]+)/);
    if (dm) { const d = departments.find((x) => x.key === dm[1]); return d ? `Отдел «${d.title}»` : ""; }
    const mm = pathname.match(/^\/modules\/([^/]+)/);
    if (mm) { const m = modules.find((x) => x.key === mm[1]); return m ? `Модуль «${m.title}»` : ""; }
    return "";
  }, [pathname, departments, modules]);

  // Доступные сотруднику разделы для командной палитры (Ctrl+K → «Перейти»).
  const navTargets = useMemo(() => {
    const t: { path: string; label: string }[] = [{ path: "/home", label: "Главная" }];
    if (pathname === "/cabinet") t.push({ path: "/cabinet", label: "Кабинет" });
    for (const d of accessibleDepts) t.push({ path: `/departments/${d.key}`, label: `Отдел «${d.title}»` });
    t.push({ path: "/team", label: "Команда" });
    t.push({ path: "/help", label: "Инструкции" });
    // Кабинет и настройки в списке были не всегда: кабинет попадал в него,
    // только когда человек УЖЕ на кабинете, а настроек не было вовсе — поиск по
    // слову «настройки» находил документы, где оно просто встречается, а сам
    // раздел не находил (03.09.2026).
    if (pathname !== "/cabinet") t.push({ path: "/cabinet", label: "Кабинет" });
    if (user?.role === "platform_admin") t.push({ path: "/settings", label: "Настройки" });
    return t;
  }, [accessibleDepts, pathname, user?.role]);

  const canOpenHelp = имеетПравоЯвно(user, ПРАВО_ИНСТРУКЦИИ);
  // ТОЛЬКО платформенный админ. Раньше условие пускало всякого, у кого хоть одно
  // право содержит action "settings" — а шаблон роли «руководитель отдела» даёт
  // wb_analytics:[view,edit,settings], то есть пункт видел каждый руководитель.
  const canOpenSettings = user?.role === "platform_admin";
  // Меню профиля раскрывается вверх, но на невысоком окне сверху места нет —
  // тот же хук, что у уведомлений, разворачивает его вниз.

  const isDeptsActive = pathname.startsWith("/departments");

  if (!user) return null;

  // Цвет отдела — из ЕДИНОГО источника (lib/dept-colors), как на «Команде» и в HR.
  const deptColor = (key: string | null | undefined) => deptColorOf(departments, key ?? "");
  // Тинт активного отдела его цветом (вместо жёстко-фиолетового glass-nav-active).
  // Подсветка выбранного отдела. Была ощутимо ярче (заливка 22→9%, рамка 42%,
  // плюс свечение вокруг): пока у отделов были разные цвета, она читалась как
  // акцент, а когда все стали фирменным фиолетовым — как «горящая» строка
  // (Максим, 26.08.2026). Свечение убрано совсем, заливка и рамка приглушены;
  // цвет подписи оставлен — это и есть ответ на вопрос «где я».
  // ⚠️ ТОЛЬКО ЦВЕТ ПОДПИСИ (Максим, 31.08.2026). Заливка и рамка убраны: выбранный
  // отдел отмечается тем же способом, что и остальные пункты меню, — цветом,
  // а не обводкой.
  const deptTint = (c: string): React.CSSProperties => ({
    background: "transparent",
    borderColor: "transparent",
    color: c,
  });

  // ── Sidebar content ─────────────────────────────────────────────────────────
  // ⚠️ ЭТО НЕ КОМПОНЕНТ, А ПОМОЩНИК РЕНДЕРА: вызывать `{SidebarContent({...})}`,
  // а НЕ `<SidebarContent/>`. Объявлена она внутри AppShell, поэтому как элемент
  // JSX получала новый тип на каждый рендер — React выбрасывал и создавал заново
  // ВСЁ поддерево меню. Из-за этого раскрытие отдела не анимировалось: <Collapse>
  // рождался уже раскрытым, начального состояния браузер не видел и анимировать
  // было нечего (Максим, 26.08.2026). Своих хуков здесь нет, поэтому вызов
  // функцией безопасен и сохраняет узлы между рендерами.
  function SidebarContent({ mobile = false }: { mobile?: boolean }) {
    if (!user) return null;
    const c = !mobile && !deskExpanded;
    // Ref своего экземпляра (не общий) — иначе useOutsideClick ломает клики, см. выше.
    const deptsRef = mobile ? deptsRefMobile : deptsRefDesktop;
    // Слот значка = ширина колонки свёрнутого рельса; значок центрируется в нём —
    // одинаковый x значка в свёрнутом и развёрнутом (все по центру, не «прыгают»).
    const ICON_SLOT = "flex w-[46px] shrink-0 items-center justify-center";

    // Пилюля пункта рельса: узкая (иконка) → при наведении ВЫЕЗЖАЕТ вправо с
    // названием (оверлеем, absolute — бар не «прыгает»). Активная = primary.
    // Свёрнутый рельс: значок в КРУЖКЕ (серый + серая обводка, как колокольчик).
    function railCircleClass(isActive?: boolean) {
      return `mx-auto flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors duration-300 ease-[cubic-bezier(.16,1,.3,1)] glass-nav ${
        isActive
          ? "glass-nav-active"
          : "border-[color-mix(in_srgb,var(--textMuted)_38%,transparent)] text-[var(--textMuted)] hover:text-[var(--text)]"
      }`;
    }
    // Развёрнутая строка (при наведении на бар / mobile): иконка + название.
    function railRowClass(isActive?: boolean) {
      return `flex items-center gap-3 rounded-[var(--radius-lg)] px-3 py-2.5 text-[12px] font-semibold transition glass-nav ${
        isActive ? "glass-nav-active" : "text-[var(--textMuted)] hover:text-[var(--text)]"
      }`;
    }

    // Единый пункт: кружок-значок ПОСТОЯННОЙ высоты (h-12, size 22) в обоих
    // режимах; при раскрытии справа добавляется ярлык. Бар «разъезжается» вправо,
    // по высоте не прыгает (значки не меняют размер).
    function NavLink({ href, icon: Icon, label, active }: { href: string; icon: React.ElementType; label: string; active?: boolean }) {
      // Значок в фикс-слоте (ширина колонки свёрнутого рельса) с центрированием —
      // одинаковый x в обоих режимах; при раскрытии справа ярлык. Высота/позиция
      // значка не меняются.
      return (
        <Link
          href={href}
          title={c ? label : undefined}
          // Пилюля ведёт КУРСОР, а выбранный пункт подсвечен на месте
          // (glass-nav-active), поэтому на активный она не заезжает —
          // иначе он выделен дважды. В свёрнутом рельсе пилюли нет вовсе:
          // там пункты — кружки, прямоугольная подсветка им не подходит.
          data-mh={c ? undefined : "item"}
          data-mh-skip={!c && active ? "1" : undefined}
          className={`group flex items-center gap-1 transition ${c ? "" : `h-11 rounded-[var(--radius-md)] pr-3 glass-nav ${active ? "glass-nav-active" : ""}`}`}
        >
          <span className={ICON_SLOT}>
            {c
              ? <span className={`${railCircleClass(active)} !mx-0`}><Icon size={22} /></span>
              : <Icon size={22} className="text-[var(--textMuted)] group-hover:text-[var(--text)]" />}
          </span>
          {!c && <span className="nav-label truncate text-[12px]">{label}</span>}
        </Link>
      );
    }

    // Текущий выбранный отдел (для значка в рельсе + подсветки).
    const activeDept = accessibleDepts.find((d) => pathname === `/departments/${d.key}`);

    // ⚠️ На этом контейнере стоял inline fontFamily: Benzin — на ВЕСЬ сайдбар.
    // Benzin дисплейный, для крупных надписей: на 12px его кириллица широкая и
    // рыхлая, подписи разделов не влезали и обрезались («Планирование п.»).
    // Меню наследует интерфейсный шрифт, Benzin остаётся заголовкам.
    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className={`flex items-center ${c ? "justify-center" : "justify-start"} gap-2.5`}>
            {logoSrc ? (
              <>
                <img
                  src={logoSrc}
                  alt="Логотип"
                  className="object-contain shrink-0 h-12 w-12"
                />
                {!c && <span className="text-[12px] font-semibold truncate">YUC</span>}
              </>
            ) : !c && (
              <div>
                <div className="field-label">YUC Portal</div>
                <div className="mt-1 text-[16px] font-semibold">Единая платформа</div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        {/* ⚠️ ОДНА ПЕРЕЕЗЖАЮЩАЯ ПОДСВЕТКА НА ВСЮ ВЕРХНЮЮ ГРУППУ.
            Раньше пилюля жила только внутри списка отделов, а «Главная»,
            «Команда» и «Инструкции» стояли снаружи и получали лишь общий
            стеклянный ховер портала — в одном меню было два разных языка
            (Максим, 28.08.2026; замер: на отделе пилюля непрозрачность 1,
            на «Инструкциях» — 0). Теперь список отделов — просто div, а
            подсветка одна на всё меню. */}
        <nav className={`${mobile ? "mt-2" : "mt-5"}`}>
          <MovingHighlight className="space-y-3">

          {/* Главная. Пункт — по праву: список пунктов должен быть ровно тем, что
              выдано в матрице (Максим, 04.09.2026). */}
          {имеетПравоЯвно(user, ПРАВО_ГЛАВНАЯ) && (
            <NavLink
              href="/home"
              icon={IconHome}
              label="Главная"
              active={pathname === "/home"}
            />
          )}

          {/* ⚠️ КАБИНЕТ — ЛИЧНЫЙ рабочий стол, а не профиль (Максим, 01.09.2026:
              «делать отдельной вкладкой и назвать её кабинет, для каждого
              сотрудника»). Стоит сразу под «Главной»: та отвечает на вопрос
              «что это за компания», кабинет — «что от меня хотят сегодня».
              Стартовым экраном после входа остаётся «Главная» (его же решение). */}
          {имеетПравоЯвно(user, ПРАВО_КАБИНЕТ) && (
            <NavLink
              href="/cabinet"
              icon={IconDashboard}
              label="Кабинет"
              active={pathname === "/cabinet"}
            />
          )}

          {/* «Дорожная карта» из рельса убрана (просьба Олега 29.07.2026): она
              встроена в НИЗ Главной (app/home/page.tsx → RoadmapSection), поэтому
              отдельный пункт дублировал вход. Сам модуль и роут /modules/roadmap
              живы — по прямой ссылке открываются как раньше. */}

          {/* Отделы. Свёрнуто — капсула (раздел + активный отдел кружком).
              Развёрнуто — пилюля «Отделы» + активный отдел строкой (с подписью);
              по КЛИКУ пилюля РАЗДВИГАЕТСЯ ВНИЗ в полный список (оверлей). */}
          {c ? (
            /* СВЁРНУТЫЙ рельс: клик по «Отделам» выкидывает список ВПРАВО (сам бар не
               разъезжается). Открытие именно по клику (просьба Олега 29.07.2026): на
               наведении меню выскакивало, когда мимо рельса просто вели курсор.
               Закрытие — повторный клик, выбор отдела или клик вне (useOutsideClick).
               Паддинг обёртки флайаута оставлен как визуальный зазор до панели. */
            <div className="relative" ref={deptsRef}>
              {activeDept ? (() => {
                const DIcon = DEPT_ICON[activeDept.key.toLowerCase()] ?? fallbackDeptIcon;
                return (
                  <div
                    className="rail-dept-capsule mx-auto flex w-12 flex-col items-center gap-1 rounded-full p-1"
                    style={{
                      background: "color-mix(in srgb, var(--text) 5%, transparent)",
                      border: "1px solid color-mix(in srgb, var(--text) 8%, transparent)",
                    }}
                  >
                    <button
                      type="button"
                      aria-label="Отделы"
                      aria-expanded={deptsFly}
                      onClick={() => setDeptsFly((v) => !v)}
                      className={`rail-dept-btn flex h-11 w-11 items-center justify-center rounded-full glass-flat transition-colors ${deptsFly ? "text-[var(--text)]" : "text-[var(--textMuted)]"}`}
                    >
                      <Building2 size={22} />
                    </button>
                    <Link href={`/departments/${activeDept.key}`} aria-label={activeDept.title} className="flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors glass-nav" style={deptTint(deptColor(activeDept.key))}>
                      <DIcon size={17} />
                    </Link>
                  </div>
                );
              })() : (
                <button
                  type="button"
                  aria-label="Отделы"
                  aria-expanded={deptsFly}
                  onClick={() => setDeptsFly((v) => !v)}
                  className={`rail-dept-btn ${railCircleClass(isDeptsActive || deptsFly)}`}
                >
                  <Building2 size={22} />
                </button>
              )}

              {deptsFly && (
                <div className="absolute left-full top-0 pl-2" style={{ zIndex: Z.FAB }}>
                  {/* Высота — до низа экрана (deptsFlyMaxH), дальше прокрутка:
                      с раскрытыми разделами список бывает длиннее экрана. */}
                  <div
                    className="yuc-unfurl-tl menu-window rail-fly-panel w-[196px] overflow-y-auto rounded-[var(--radius-lg)] p-1"
                    style={{ ...glassPillStyle(mode === "dark"), background: "var(--surfaceElevated)", maxHeight: deptsFlyMaxH }}
                  >
                    <div className="flex h-9 items-center gap-1 px-2">
                      <Building2 size={16} className="text-[var(--textMuted)]" />
                      <span className="field-label truncate">Отделы</span>
                    </div>
                    {/* ⚠️ РАЗДЕЛЫ ОТДЕЛА РАСКРЫВАЮТСЯ И ЗДЕСЬ (Максим, 30.09.2026:
                        «в свёрнутом меню нажатие на отдел не раскрывает его
                        разделы»). Раньше флайаут был плоским списком отделов:
                        нажатие уводило прямо на страницу отдела, и второго
                        уровня в свёрнутом рельсе не существовало вовсе — тогда
                        как в развёрнутом баре он есть. Теперь строка отдела
                        ведёт себя ровно так же, как в развёрнутом баре: отдел с
                        разделами — кнопка-аккордеон, отдел без разделов —
                        обычная ссылка на его дашборд. Состояние раскрытия общее
                        (openDept), поэтому свернуть/развернуть бар не сбрасывает
                        то, что человек открыл. */}
                    <MovingHighlight>
                    {accessibleDepts.map((dept) => {
                      const DIcon = DEPT_ICON[dept.key.toLowerCase()] ?? fallbackDeptIcon;
                      const active = pathname === `/departments/${dept.key}`;
                      // Тот же отбор, что на странице отдела и в развёрнутом баре:
                      // списки обязаны совпадать, иначе в меню появятся пункты,
                      // которых на странице нет.
                      const mods = visibleDeptTabs(dept.key, pickDeptModules(portalModules, dept.key, user), user);
                      // Открыт тот отдел, который выбрали; пока не выбирали — активный.
                      const expanded = openDept === dept.key || (openDept === null && active);
                      // Активный раздел есть только у отдела, чья страница СЕЙЧАС открыта.
                      const published = active && activeSection.deptKey === dept.key ? activeSection : null;
                      const rowClass = `rail-fly-row group flex h-9 w-full items-center gap-2 rounded-[var(--radius-md)] pl-1 pr-2 transition glass-nav ${active ? "font-semibold" : "text-[var(--textMuted)] hover:text-[var(--text)]"}`;
                      const rowStyle = active ? deptTint(deptColor(dept.key)) : undefined;
                      const rowInner = (
                        <>
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            style={active ? undefined : { color: deptColor(dept.key), background: `color-mix(in srgb, ${deptColor(dept.key)} 12%, transparent)` }}
                          >
                            <DIcon size={16} />
                          </span>
                          <span className="flex-1 truncate text-left text-[12px] font-semibold">{dept.title}</span>
                        </>
                      );
                      return (
                        <div key={dept.key}>
                          {mods.length > 0 ? (
                            <button
                              type="button"
                              data-mh="item"
                              data-mh-skip={active ? "1" : undefined}
                              aria-expanded={expanded}
                              onClick={() => setOpenDept(expanded ? "" : dept.key)}
                              className={`${rowClass} glass-flat`}
                              style={rowStyle}
                            >
                              {rowInner}
                              <IconChevronDown
                                size={14}
                                className={`shrink-0 opacity-60 transition-transform ${expanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          ) : (
                            <Link
                              data-mh="item"
                              data-mh-active={active ? "1" : undefined}
                              href={`/departments/${dept.key}`}
                              onClick={() => { markOpenDeptDashboard(dept.key); setDeptsFly(false); }}
                              className={rowClass}
                              style={rowStyle}
                            >
                              {rowInner}
                            </Link>
                          )}
                          <Collapse open={expanded && mods.length > 0}>
                            {/* Ветка отдела: только РАЗДЕЛЫ, без бегающей рамки —
                                как в развёрнутом баре. Адрес без подраздела
                                НАМЕРЕННО: какой подраздел открыть, знает только
                                страница отдела. */}
                            <div className="nav-tree rail-fly-tree pt-0.5">
                              {mods.map((m) => {
                                const activeMod = !!published && published.moduleKey === m.key;
                                return (
                                  <Link
                                    key={m.key}
                                    href={`/departments/${dept.key}`}
                                    data-key={m.key}
                                    data-active={activeMod ? "1" : undefined}
                                    onClick={() => { markOpenDeptModule(dept.key, m.key); setDeptsFly(false); }}
                                    className="nav-tree-item rail-fly-sub group relative flex h-8 w-full items-center text-left text-[12px]"
                                  >
                                    {activeMod && <span className="nav-tree-dot" />}
                                    <span
                                      className="nav-tree-label flex-1 truncate"
                                      data-state={activeMod ? "active" : undefined}
                                    >
                                      {m.label}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                    </MovingHighlight>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── ОТДЕЛЫ СПИСКОМ, МОДУЛИ РАСКРЫТИЕМ ПОД НИМИ ──────────────────
               Структура референса PLUX: вся навигация слева, плоский список
               разделов, вложенность раскрывается на месте. Прежняя капсула
               «Отделы» с выпадающим оверлеем убрана — она была вторым уровнем
               меню, а в референсе уровень один (Максим, 26.08.2026).
               Список модулей берём тем же отбором, что и страница отдела
               (pickDeptModules), иначе в меню появятся пункты, которых там нет. */
            /* ДВА указателя, а не один: выбранный отдел подсвечен НА МЕСТЕ своим
               цветом, а переезжающая пилюля показывает только курсор. Одна общая
               подсветка уезжала с выбранного пункта и выглядела прыжком —
               пропадал ответ на вопрос «где я» (Максим, 26.08.2026).
               Поэтому data-mh-active на строках отделов НЕ ставим: без активной
               цели пилюля прячется, когда курсор ушёл. А на сам выбранный отдел
               она не заезжает (data-mh-skip) — иначе он выделен дважды. */
            <div ref={deptsRef} className="flex flex-col gap-0.5">
              {accessibleDepts.map((dept) => {
                const DIcon = DEPT_ICON[dept.key.toLowerCase()] ?? fallbackDeptIcon;
                const isActiveDept = pathname === `/departments/${dept.key}`;
                // Открыт тот отдел, который выбрали; пока не выбирали — активный.
                const expanded = openDept === dept.key || (openDept === null && isActiveDept);
                // Разделы открытого отдела берём из публикации страницы, для
                // остальных — из модулей: синтетические вкладки чужого отдела
                // взять неоткуда, пока в него не зашли.
                // ⚠️ Активный раздел есть только у отдела, чья страница СЕЙЧАС открыта.
                // Проверки на адрес не было, и опубликованный раздел продолжал
                // подсвечиваться после ухода в другой раздел портала: на «Команде»
                // светились сразу два пункта — сама «Команда» и запомненный раздел
                // отдела, стоило раскрыть его список (Максим, 31.08.2026).
                const onThisDept = pathname === `/departments/${dept.key}`;
                const published = onThisDept && activeSection.deptKey === dept.key ? activeSection : null;
                const mods = visibleDeptTabs(dept.key, pickDeptModules(portalModules, dept.key, user), user);
                return (
                  <div key={dept.key}>
                    <button
                      type="button"
                      data-mh="item"
                      data-mh-skip={isActiveDept ? "1" : undefined}
                      onClick={() => setOpenDept(expanded ? "" : dept.key)}
                      aria-expanded={expanded}
                      className={`glass-flat group relative z-[1] flex h-9 w-full items-center gap-1 rounded-[var(--radius-md)] border pr-2 ${
                        isActiveDept ? "font-semibold" : "border-transparent"
                      }`}
                      style={isActiveDept ? deptTint(deptColor(dept.key)) : undefined}
                    >
                      <span className={ICON_SLOT}><DIcon size={17} /></span>
                      {/* У активного отдела цвет задаёт плашка отдела (deptTint) —
                          .nav-label ей мешать не должен. */}
                      <span className={`flex-1 truncate text-left text-[12px] ${isActiveDept ? "font-semibold" : "nav-label"}`}>{dept.title}</span>
                      <IconChevronDown
                        size={14}
                        className={`shrink-0 opacity-60 transition-transform ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    <Collapse open={expanded}>
                      {/* Ветка отдела: только РАЗДЕЛЫ. Подразделы в меню не
                          заводим — в референсе они идут строкой вкладок вверху
                          содержимого — общим Segmented. Иконок на этом
                          уровне нет: вложенность читается отступом и
                          направляющей (.nav-tree). */}
                      {/* Внутри ветки НЕТ бегающей рамки: в референсе обведён
                          только отдел, разделы и подразделы выделяются одним
                          цветом (Максим, 26.08.2026). MovingHighlight здесь
                          рисовал бы вторую рамку и спорил с рамкой отдела. */}
                      <div className="nav-tree pt-0.5">
                          {mods.map((m) => {
                            const activeMod = !!published && published.moduleKey === m.key;
                            return (
                              <Link
                                key={m.key}
                                href={`/departments/${dept.key}`}
                                data-key={m.key}
                                data-active={activeMod ? "1" : undefined}
                                // ⚠️ БЕЗ ПОДРАЗДЕЛА — НАМЕРЕННО. Какой подраздел открыть,
                                // знает только страница отдела: у неё список видимых
                                // подразделов (отфильтрованный по правам) и память
                                // «где человек остановился в этом разделе». Сайдбар
                                // называет РАЗДЕЛ, страница открывает первый видимый
                                // подраздел (или запомненный) и подсвечивает ровно его.
                                onClick={() => markOpenDeptModule(dept.key, m.key)}
                                className="nav-tree-item group relative flex h-8 w-full items-center text-left text-[12px]"
                              >
                                {activeMod && <span className="nav-tree-dot" />}
                                {/* ⚠️ Цвет — на span, НЕ на <a>. В globals.css есть
                                    незалойеренное `a { color: inherit }`, а утилиты
                                    Tailwind лежат в @layer utilities и проигрывают
                                    ему: класс на ссылке молча не работает. */}
                                <span
                                  className="nav-tree-label flex-1 truncate"
                                  data-state={activeMod ? "active" : undefined}
                                >
                                  {m.label}
                                </span>
                              </Link>
                            );
                          })}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          )}

          {/* «Команда» — люди компании: структура, сотрудники, роли, профиль.
              Пункт видят ВСЕ: каждому как минимум открыт свой профиль, а что
              покажут дальше, решают права внутри раздела (Максим, 27.08.2026). */}
          {/* «Команда» — по тому же именному праву (tab:team:__roster), без
              админского обхода: пункты меню должны быть ровно теми, что выданы. */}
          {имеетПравоЯвно(user, ПРАВО_КОМАНДА) && (
            <NavLink
              href="/team"
              icon={IconUsers}
              label="Команда"
              active={pathname.startsWith("/team")}
            />
          )}

          {/* Инструкции */}
          {canOpenHelp && (
            <NavLink
              href="/help"
              icon={IconBook}
              label="Инструкции"
              active={pathname.startsWith("/help")}
            />
          )}
          {/* «Пользователи» и «Настройки» перенесены в попап профиля ниже */}
          </MovingHighlight>
        </nav>

        {/* Notifications — раскрытие по клику.
            mt-auto: панель теперь во всю высоту экрана, и нижний блок
            (уведомления → профиль → тема → «Свернуть») прижат к низу, как в
            референсе. Когда пунктов много и появляется прокрутка, авто-отступ
            схлопывается сам. */}
        {/* ⚠️ ВТОРАЯ ПИЛЮЛЯ — НА НИЖНЮЮ ГРУППУ. Отдельная от верхней намеренно:
            одна общая на весь рельс ездила бы через разделитель на пол-экрана.
            Переключатель темы внутрь обёртки попадает, но data-mh на нём НЕТ —
            у него своя подсветка выбранного, две спорили бы. */}
        {/* ⚠️ w-full + flex flex-col обязательны. Раньше эти строки были прямыми
            детьми колонки сайдбара; обёртка-блок ломала им раскладку: без w-full
            она сжималась по содержимому (44 px вместо ширины рельса), а без
            flex-col переставал работать self-center у переключателя темы, и
            иконки съезжали влево (замер: 1100 отличающихся точек в полосе
            рельса на всех страницах). */}
        <MovingHighlight className="mt-auto w-full flex flex-col">
        {/* ⚠️ УВЕДОМЛЕНИЯ И ПРОФИЛЬ ОТСЮДА УБРАНЫ (Максим, 28.08.2026): они
            переехали в панель в строке заголовка — см. components/top-panel. В рельсе
            остались только тема и «Свернуть»: это действия над самим меню. */}
        {/* ⚠️ ПЕРЕКЛЮЧАТЕЛЬ ТЕМЫ ОТСЮДА УБРАН (Максим, 28.08.2026): он переехал
            в панель строки заголовка, одной кнопкой — как на экране входа.
            Пара «солнце/луна» в плашке занимала строку рельса и дублировала то,
            что и так видно по самой теме. */}
        {/* Кнопка «закрепить / свернуть» главное меню (десктоп) */}
        {!mobile && (
          <button
            type="button"
            onClick={toggleCollapsed}
            // ⚠️ Без data-mh: пилюлю с этой кнопки Максим убрал (28.08.2026).
            // Она не пункт меню, а действие над самим меню — и в общий ряд
            // «куда перейти» не входит.
            data-tooltip={collapsed ? "Закрепить меню открытым" : "Свернуть меню"}
            aria-label={collapsed ? "Закрепить меню открытым" : "Свернуть меню"}
            className={`glass-flat mt-2 flex items-center text-[var(--textMuted)] transition-colors hover:text-[var(--text)] ${c ? "mx-auto h-9 w-9 justify-center rounded-full" : "w-full gap-2.5 rounded-[var(--radius-md)] px-3 py-2 text-[12px]"}`}
          >
            {collapsed ? <ChevronsRight size={c ? 20 : 17} className="shrink-0" /> : <ChevronsLeft size={17} className="shrink-0" />}
            {!c && <span className="truncate">{collapsed ? "Закрепить" : "Свернуть"}</span>}
          </button>
        )}
        </MovingHighlight>
      </div>
    );
  }


  return (
    <div className="min-h-dvh bg-[var(--bg)] text-[var(--text)]">
      {/* Полоса ожидания по верхней кромке (№29). Сама следит за запросами в
          полёте — размечать экраны не нужно, см. lib/top-progress.ts. */}
      <TopProgress />
      <DevRequestFab isAdmin={user?.role === "platform_admin"} currentUserId={user?.id ?? ""} currentLocation={aiLocation} navTargets={navTargets} />

      {/* ── Mobile: плавающий ☰-квадрат (слева) — открывает меню.
          Вместо full-width топ-бара: 3 парящих элемента (☰ · табы · правый тулбар). */}
      {/* ⚠️ ВЕРХНЯЯ ПОЛОСА НА УЗКОМ ЭКРАНЕ ЗАНЯТА ЦЕЛИКОМ (Максим, 31.08.2026).
          Раньше здесь висела одна кнопка меню, а под неё резервировалось 62 px
          высоты — справа оставалась пустая лента во всю ширину, и заголовок
          съезжал вниз. Теперь в этой же строке справа стоит панель портала
          (уведомления, чат, настройки, тема, профиль): она снята с шапки экрана
          на узких ширинах, см. `tab-header`. */}
      {!isDesktop && (
        <div
          className="lg:hidden fixed top-2 left-3 right-3 z-40 flex items-center justify-between gap-2"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Открыть меню"
            className="inline-flex h-[47px] w-[47px] items-center justify-center text-[var(--text)]"
            style={{
              borderRadius: "var(--radius-md)",
              background: "var(--nav-top-bg, color-mix(in srgb, var(--bg) 68%, transparent))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
            }}
          >
            <Menu size={20} />
          </button>
          <TopPanel className="shrink-0" />
        </div>
      )}

      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        onMouseEnter={() => setMobileOpen(true)}
        aria-label="Открыть меню"
        className="hidden fixed top-2 left-3 z-40 inline-flex h-[47px] w-[47px] items-center justify-center text-[var(--text)]"
        style={{
          borderRadius: "var(--radius-md)",
          background: "var(--nav-top-bg, color-mix(in srgb, var(--bg) 68%, transparent))",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
          boxShadow: "0 14px 34px -14px rgba(0,0,0,0.4), inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent)",
        }}
      >
        <Menu size={20} />
      </button>


      {/* ── Mobile drawer ────────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 flex" style={{ zIndex: Z.NAV_OVERLAY }}>
          {/* Прозрачный слой — только для закрытия по клику вне карточки.
              Без затемнения/блюра: контент вокруг виден чётко, как на десктопе. */}
          <div className="absolute inset-0" onClick={() => setMobileOpen(false)} />
          {/* Плавающая карточка ПО ВЫСОТЕ КОНТЕНТА (как навигация на десктопе),
              а не панель во весь экран: скругление, blur, тень, поле от краёв;
              max-h + скролл, если пунктов много. */}
          <aside
            className="relative z-10 m-3 flex max-h-[calc(100dvh-24px)] w-[256px] max-w-[85vw] flex-col self-start overflow-y-auto scrollbar-autohide px-2 py-5"
            style={{
              borderRadius: "var(--nav-side-radius, var(--radius-lg))",
              background: "var(--nav-side-bg, color-mix(in srgb, var(--surface) 88%, transparent))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
              boxShadow: "0 18px 44px -12px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, #ffffff 10%, transparent)",
            }}
          >
            {SidebarContent({ mobile: true })}
          </aside>
        </div>
      )}

      {/* ── Layout: единственный mount children ─────────────────────────────── */}
      {/* {children} монтируется РОВНО ОДИН РАЗ. Раньше было два <main>
          (desktop + mobile): каждый модуль исполнял эффекты и фетчи дважды,
          а дубли id/оверлеев требовали guard'ов в потребителях.
          Разница раскладок — только CSS-классами на общих элементах. */}
      <div className="flex lg:min-h-dvh">
        <aside
          data-nav-rail
          className={`hidden lg:flex flex-col justify-start fixed left-0 top-0 h-dvh z-nav px-3 transition-[width] duration-300 ease-[cubic-bezier(.16,1,.3,1)] ${deskExpanded ? "w-[256px]" : "w-[76px]"}`}
        >
          <div
            // ⚠️ Пока открыто меню профиля, колонка НЕ прокручивается.
            // Меню вырастает вверх внутри этой колонки, а она прокручиваемая —
            // поэтому меню упиралось в её высоту, получало собственную полосу
            // прокрутки и обрезалось: «Выйти» уходил за край (Максим, 26.08.2026).
            // Снимая прокрутку на время, даём меню растянуться по своей высоте.
            // Содержимое рельса под ним всё равно закрыто самим меню.
            // Панель занимает ВСЮ высоту экрана: flex-1 внутри h-dvh + снятый
            // потолок maxHeight. Раньше было `calc(100dvh - 80px)` и отступы
            // pt-3/pb-3 у обёртки — меню висело карточкой с зазорами сверху и
            // снизу (Максим, 26.08.2026).
            className={`flex flex-col flex-1 min-h-0 py-5 w-full px-2 ${deskExpanded ? "overflow-y-auto scrollbar-autohide-tight" : ""}`}
            style={{
              maxHeight: "100dvh",
              borderRadius: "var(--nav-side-radius, var(--radius-lg))",
              background: "var(--nav-side-bg, color-mix(in srgb, var(--surface) 70%, transparent))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid color-mix(in srgb, var(--text) 10%, transparent)",
              boxShadow: "0 18px 44px -12px rgba(0,0,0,0.45), inset 0 1px 0 color-mix(in srgb, #ffffff 10%, transparent)",
            }}
          >
            {SidebarContent({})}
          </div>
        </aside>

        <div className={`relative min-w-0 flex-1 transition-[padding] duration-300 ease-[cubic-bezier(.16,1,.3,1)] ${collapsed ? "lg:pl-[76px]" : "lg:pl-[256px]"}`}>
          <main className="px-4 pb-5 pt-[63px] lg:px-6 lg:py-6">{children}</main>
        </div>
      </div>

      {/* ── Sticky horizontal scrollbar (portal-wide) ───────────────────────── */}
      <StickyHScrollbar />
    </div>
  );
}
